import { get, find, map, filter, every, slice } from 'lodash';
import { getLearningPathBySignDocIdAPI } from './api';
import { SignDocTypes, isSignDocComplete } from '../Components/SignDoc/SignDocLib';
import { HIVE_STATUS } from '../Components/LearningPath/LearningPathLib';
import { isLearningPathUnordered } from '../Utils/SignDocs/SignDocs';

// Icon mapping imports
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import QuizIcon from '@mui/icons-material/Quiz';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import React from 'react';

/**
 * Fetches and transforms learning path data for use in the UI
 * @param {string} signDocId - ID of the root sign doc
 * @returns {Promise<{items: Array, error: string|null}>} - Formatted learning path items or error
 */
export const getLearningPathForLearner = async (signDocId) => {
  try {
    // Use the existing API endpoint directly
    const response = await getLearningPathBySignDocIdAPI({ signDocId });
    
    if (!response || !response.nodes || !response.edges) {
      console.log("Error getting learning path data: Invalid response format");
      return { items: [], error: "No learning path data available" };
    }

    // Transform graph data into linear sequence
    const nodeMap = new Map(map(response.nodes, node => [node.id, node]));
    const edgesBySource = response.edges.reduce((acc, edge) => {
      if (!acc[edge.source]) acc[edge.source] = [];
      acc[edge.source].push(edge);
      return acc;
    }, {});
    
    // Build ordered sequence of items
    let currentNodeId = response.rootSignDocId;
    const orderedItems = [];
    
    // First add root node
    if (nodeMap.has(currentNodeId)) {
      const rootNode = nodeMap.get(currentNodeId);
      orderedItems.push({
        signDocId: rootNode.id,
        signDoc: {
          docName: get(rootNode, 'data.docName'),
          description: get(rootNode, 'data.description'),
          imgSrc: get(rootNode, 'data.imgSrc'),
          type: rootNode.type,
          doNotForceCompletion: get(rootNode, 'data.doNotForceCompletion'),
          linkUrl: get(rootNode, 'data.linkUrl'),
          additionalData: get(rootNode, 'data.additionalData', {}),
        },
        progressPercent: get(rootNode, 'data.progressPercentage', 0),
        viewed: get(rootNode, 'data.viewed', false),
        signed: get(rootNode, 'data.signed', false),
        isRoot: true,
        completedOnDate: get(rootNode, 'data.completedOnDate', null),
        examScore: get(rootNode, 'data.examScore', null)
      });
    }
    
    // Then follow edges to build sequence
    while (edgesBySource[currentNodeId] && edgesBySource[currentNodeId].length > 0) {
      const nextEdge = edgesBySource[currentNodeId][0]; // Take first child
      currentNodeId = nextEdge.target;
      
      if (nodeMap.has(currentNodeId)) {
        const node = nodeMap.get(currentNodeId);
        orderedItems.push({
          signDocId: node.id,
          signDoc: {
            docName: get(node, 'data.docName'),
            description: get(node, 'data.description'),
            imgSrc: get(node, 'data.imgSrc'),
            type: node.type,
            doNotForceCompletion: get(node, 'data.doNotForceCompletion'),
            linkUrl: get(node, 'data.linkUrl'),
            additionalData: get(node, 'data.additionalData', {}),
          },
          progressPercent: get(node, 'data.progressPercentage', 0),
          viewed: get(node, 'data.viewed', false),
          signed: get(node, 'data.signed', false),
          isRequired: nextEdge.required,
          completedOnDate: get(node, 'data.completedOnDate', null),
          examScore: get(node, 'data.examScore', null)
        });
      }
    }
    
    // Compute unordered flag once (avoid O(N²) lookup per item)
    const rootItem = find(orderedItems, i => get(i, 'isRoot', false));
    const isUnordered = isLearningPathUnordered(get(rootItem, 'signDoc'));

    // Add status and UI properties
    const items = map(orderedItems, (item, index, arr) => ({
      ...item,
      icon: getIconForSignDocType(get(item, 'signDoc.type', 'default')),
      status: determineItemStatus(item, index, arr, isUnordered),
      next: index < arr.length - 1 ? arr[index + 1].signDocId : null,
      previous: index > 0 ? arr[index - 1].signDocId : null
    }));
    
    return {
      items,
      rootSignDocId: response.rootSignDocId,
      error: null
    };
  } catch (error) {
    console.log("Error getting learning path for learner:", error);
    return {
      items: [],
      error: "Failed to load learning path. Please try again later."
    };
  }
};

/**
 * Determines the status of a learning path item based on its progress.
 *
 * Two modes (driven by the LearningPath root SignDoc's `additionalData.unorderedLearningPathTasks`):
 *  - Sequential (default): only the first non-completed, non-root item is active; the rest are locked.
 *  - Free-path (strict === true): every non-completed item is active; nothing is locked.
 *
 * @param {object} item - The item to evaluate (carries `signDoc`, `progressPercent`, `viewed`, `signed`, `isRoot`).
 * @param {number} index - The item's index in `allItems` (unused directly; kept for caller signature parity).
 * @param {object[]} allItems - The full ordered list of items in the learning path, used to locate the root item.
 * @returns {string} One of `HIVE_STATUS.COMPLETED`, `HIVE_STATUS.ACTIVE`, or `HIVE_STATUS.LOCKED`.
 */
const determineItemStatus = (item, index, allItems, isUnordered) => {

  // Root item should not affect the active status logic, but should reflect completion status
  if (get(item, 'isRoot', false)) {
    return isSignDocComplete({
      type: get(item, 'signDoc.type'),
      progressPercent: item.progressPercent,
      viewed: item.viewed,
      signed: item.signed
    }) ? HIVE_STATUS.COMPLETED
      : HIVE_STATUS.ACTIVE;
  }

  // Free-path mode: completed → completed, otherwise → active. Never locked.
  if (isUnordered) {
    return isSignDocComplete({
      type: get(item, 'signDoc.type'),
      progressPercent: item.progressPercent,
      viewed: item.viewed,
      signed: item.signed
    }) ? HIVE_STATUS.COMPLETED : HIVE_STATUS.ACTIVE;
  }

  // Filter to get only visible items (non-root) for logical progression
  const visibleItems = filter(allItems, i => !get(i, 'isRoot', false));
  const visibleIndex = visibleItems.findIndex(i => i.signDocId === item.signDocId);

  // For visible items:
  // If this item is completed, mark it completed
  if (isSignDocComplete({
    type: get(item, 'signDoc.type'),
    progressPercent: item.progressPercent,
    viewed: item.viewed,
    signed: item.signed
  })) {
    return HIVE_STATUS.COMPLETED;
  }

  // Check if all previous visible items are completed
  const allPreviousCompleted = every(
    slice(visibleItems, 0, visibleIndex),
    prevItem => isSignDocComplete({
      type: get(prevItem, 'signDoc.type'),
      progressPercent: prevItem.progressPercent,
      viewed: prevItem.viewed,
      signed: prevItem.signed
    })
  );

  // Check if there's already an active visible item before this one
  const hasActiveItemBefore = find(
    slice(visibleItems, 0, visibleIndex),
    prevItem => !isSignDocComplete({
      type: get(prevItem, 'signDoc.type'),
      progressPercent: prevItem.progressPercent,
      viewed: prevItem.viewed,
      signed: prevItem.signed
    })
  );

  // Only make this item active if all previous visible items are completed AND there's no active item before this one
  if (allPreviousCompleted && !hasActiveItemBefore) {
    return HIVE_STATUS.ACTIVE;
  }

  // Otherwise, it's locked
  return HIVE_STATUS.LOCKED;
};

/**
 * Returns the appropriate icon for a sign doc type
 * @param {string} type - SignDoc type
 * @returns {React.Element} - Icon element
 */
const getIconForSignDocType = (type) => {
  switch (type) {
    case SignDocTypes.REGULAR_COURSE:
      return <SchoolIcon />;
    case SignDocTypes.REGULAR_DOC:
      return <AssignmentIcon />;
    case SignDocTypes.EXAM:
      return <QuizIcon />;
    case SignDocTypes.VIDEO:
      return <VideoLibraryIcon />;
    default:
      return <MenuBookIcon />;
  }
};

